import { forwardRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Slide, { SlideProps } from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { Breakpoint } from '@mui/material';

import PopupContext from './Popup.context';

import usePopupStyles from './styles';

const Transition = forwardRef((props: SlideProps, ref) => (
    <Slide direction="up" ref={ref} {...props} />
));

Transition.displayName = 'Transition';

export interface PopupProps {
    title: React.ReactNode | string;
    children: React.ReactNode;
    visible: boolean;
    setVisible: React.Dispatch<React.SetStateAction<boolean>>;
    onClose?: () => void;
    width?: false | Breakpoint | undefined;
    fullHeight?: boolean;
    fullScreen?: boolean;
    scroll?: 'paper' | 'body';
}

const Popup = ({
    title,
    children,
    visible,
    setVisible,
    onClose = () => {},
    width = 'md',
    fullHeight = false,
    fullScreen = false,
    scroll = 'paper'
}: PopupProps) => {
    const classes = usePopupStyles();

    const handleCLose = () => {
        setVisible(false);
        onClose();
    };

    useEffect(() => {
        if (visible) {
            const htmlEl = document.querySelector('html');
            if (htmlEl) {
                const currensScroll = htmlEl.scrollTop;
                htmlEl.style.position = 'fixed';
                setTimeout(() => {
                    htmlEl.style.position = '';
                    htmlEl.scrollTo({ top: currensScroll });
                });
            }
        }
    }, [visible]);

    return (
        <PopupContext.Provider value={{ visible, setVisible }}>
            <Dialog
                open={visible}
                maxWidth={width}
                classes={{ paper: fullHeight ? classes.root : classes.r2 }}
                fullScreen={fullScreen}
                TransitionComponent={Transition}
                fullWidth
                scroll={scroll}
                sx={{
                    '& .MuiDialog-paper': { overscrollBehavior: 'contain' }
                }}
            >
                <DialogTitle>
                    <div className={classes.titleWrapper}>
                        <Typography variant="h4" component="h4">
                            {title}
                        </Typography>
                        <IconButton
                            classes={{ root: classes.closeButton }}
                            onClick={handleCLose}
                            size="large"
                        >
                            <CloseIcon color="primary" style={{ fontSize: 20 }} />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent className={classes.content}>{children}</DialogContent>
            </Dialog>
        </PopupContext.Provider>
    );
};

export default Popup;
